import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, Download, CheckCircle2, AlertTriangle, XCircle, CalendarClock, FileSearch } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { parseDelimited } from '@/utils/csv-helper';

/** The sheet's columns, in order. The first five are required. */
const COLUMNS = ['lrno', 'appointment_date', 'appointment_slot', 'po_number', 'po_expiry_date', 'appointment_id'] as const;

// Sheet headings as people actually write them → the keys the server reads.
const HEADER_KEYS: Record<string, string> = {
  lrno: 'lrNo', lr: 'lrNo', lrnumber: 'lrNo',
  appointmentdate: 'appointmentDate', date: 'appointmentDate',
  appointmentslot: 'appointmentSlot', slot: 'appointmentSlot', appointmenttime: 'appointmentSlot', timeslot: 'appointmentSlot',
  ponumber: 'poNumber', po: 'poNumber', pono: 'poNumber', ponumbers: 'poNumber',
  poexpirydate: 'poExpiryDate', poexpiry: 'poExpiryDate', expirydate: 'poExpiryDate',
  appointmentid: 'appointmentId', appointmentref: 'appointmentId', slotid: 'appointmentId',
};
const canonical = (h: string) => HEADER_KEYS[h.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z0-9]/g, '')] ?? h.trim();

interface ImportRow {
  row: number; lrNo: string; consignee?: string | null; courier?: string | null;
  appointmentDate?: string | null; appointmentSlot?: string | null; poNumber?: string | null; poExpiryDate?: string | null;
  appointmentId?: string | null; canBook: boolean; ok: boolean; saved?: boolean; errors: string[]; warnings: string[];
}
interface ImportResult { dryRun: boolean; total: number; valid: number; invalid: number; saved: number; rows: ImportRow[] }
interface BookResult { lrNo: string; ok: boolean; recorded?: boolean; appointmentRef?: string | null; message: string }

const fmtDay = (iso?: string | null) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : iso ?? '—');

/**
 * Import appointments from a sheet, check them, save them, then book the ones you tick.
 *
 * Three deliberate steps, because the last one talks to a courier and cannot be taken back:
 *   1. Check file   — every row validated on the server (LR exists, dates, slot, PO), nothing saved.
 *   2. Save         — the valid rows go onto their LR's appointment as "Ready"; invalid ones are skipped.
 *   3. Book selected — the ticked rows are sent to Delhivery (own / manual couriers are recorded
 *                      here, they have no API), and each row shows what the courier answered.
 */
export function ImportAppointmentsModal({
  isOpen, onClose, onChanged, slots,
}: { isOpen: boolean; onClose: () => void; onChanged?: () => void; slots: readonly string[] }) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState<'check' | 'save' | 'book' | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [booked, setBooked] = useState<Record<string, BookResult>>({});

  useEffect(() => {
    if (!isOpen) return;
    setText(''); setFileName(''); setResult(null); setBusy(null); setSelected([]); setBooked({});
  }, [isOpen]);

  const parsed = useMemo(() => parseDelimited(text, { mapHeader: canonical }).rows, [text]);
  const hasLrColumn = parsed.length > 0 && Object.prototype.hasOwnProperty.call(parsed[0], 'lrNo');

  /**
   * The sample file lives in the portal itself (`/samples/appointments-import-template.csv`), so the
   * button is a plain link: nothing is built in the browser, nothing can be cancelled mid-download,
   * and the same address can be sent to a merchant or opened straight in Excel.
   */
  const SAMPLE_URL = '/samples/appointments-import-template.csv';

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => { setText(String(ev.target?.result ?? '')); setFileName(f.name); setResult(null); setBooked({}); setSelected([]); };
    r.readAsText(f);
    e.target.value = '';
  };

  const run = async (dryRun: boolean) => {
    if (!parsed.length) { toast.error('No rows found — upload the CSV or paste it below.'); return; }
    if (!hasLrColumn) { toast.error(`The first line must be the headings: ${COLUMNS.join(', ')}`); return; }
    setBusy(dryRun ? 'check' : 'save');
    try {
      const r = (await api.post<ImportResult>('/appointments/import', { rows: parsed, dryRun }, { timeout: 120_000 })).data;
      setResult(r);
      setBooked({});
      if (dryRun) {
        if (r.invalid) toast.warning(`${r.valid} row(s) ready, ${r.invalid} with problems — fix them in the sheet or save only the good ones.`);
        else toast.success(`All ${r.valid} row(s) look good.`);
      } else {
        toast.success(`${r.saved} appointment(s) saved as Ready${r.invalid ? ` · ${r.invalid} skipped` : ''}. Tick the ones to book and press "Book selected".`);
        setSelected(r.rows.filter((x) => x.saved).map((x) => x.lrNo));
        onChanged?.();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Import failed');
    } finally {
      setBusy(null);
    }
  };

  const bookSelected = async () => {
    if (!selected.length) return;
    setBusy('book');
    try {
      const r = (await api.post<{ booked: number; failed: number; results: BookResult[] }>(
        '/appointments/book-selected', { lrNos: selected }, { timeout: 300_000 },
      )).data;
      setBooked(Object.fromEntries(r.results.map((x) => [x.lrNo, x])));
      if (r.failed) toast.warning(`${r.booked} booked, ${r.failed} not booked — see the reason on each row.`);
      else toast.success(`All ${r.booked} booked.`);
      setSelected(r.results.filter((x) => !x.ok).map((x) => x.lrNo));
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Booking failed');
    } finally {
      setBusy(null);
    }
  };

  const savedRows = result && !result.dryRun ? result.rows.filter((x) => x.saved) : [];
  const toggle = (lr: string) => setSelected((s) => (s.includes(lr) ? s.filter((x) => x !== lr) : [...s, lr]));
  const allTicked = savedRows.length > 0 && savedRows.every((x) => selected.includes(x.lrNo));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Import appointments"
      subtitle="Upload a sheet, check it, save it — then book the rows you tick" maxWidth="6xl">
      <div className="space-y-4">
        {/* The format, said once, where the person is looking. */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="font-medium text-slate-800">
              Columns: <code className="text-[11px]">{COLUMNS.join(', ')}</code>
            </div>
            <a href={SAMPLE_URL} download="appointments-import-template.csv"
              className="px-2.5 py-1 border border-slate-300 rounded-md text-xs flex items-center gap-1 hover:bg-white shrink-0">
              <Download size={12} /> Sample file
            </a>
          </div>
          <div>The first five are <b>required</b>; <b>appointment_id</b> only when changing a booking the courier already gave you.</div>
          <div>
            Dates day first — <code>24-09-2026</code> (or <code>2026-09-24</code>). Appointment date today or later; PO expiry today or later and
            not before the appointment date. Several POs in one cell: separate them with <code>;</code>. The LR must already exist in our system.
          </div>
          <div>Slots: {slots.join(' · ')}</div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="px-3 py-2 border border-slate-300 rounded-lg text-sm flex items-center gap-1.5 cursor-pointer hover:bg-slate-50">
            <Upload size={14} /> Choose CSV file
            <input type="file" accept=".csv,.txt,text/csv" onChange={onFile} className="hidden" />
          </label>
          <span className="text-xs text-slate-500">{fileName || 'or paste the rows below (Excel: File → Save as → CSV)'}</span>
          {parsed.length > 0 && <span className="text-xs text-slate-700 ml-auto">{parsed.length} row{parsed.length === 1 ? '' : 's'} in the file</span>}
        </div>
        <textarea value={text} onChange={(e) => { setText(e.target.value); setResult(null); setBooked({}); setSelected([]); }} rows={4}
          placeholder={`${COLUMNS.join(',')}\nGL110064188,24-09-2026,09:00 AM-12:00 PM,P5584399,30-09-2026,`}
          className="input w-full font-mono text-xs resize-y" />

        {result && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs flex flex-wrap items-center gap-3">
              <span className="font-medium text-slate-800">{result.dryRun ? 'Checked' : 'Saved'} {result.total} row{result.total === 1 ? '' : 's'}:</span>
              <span className="text-emerald-700">{result.valid} OK</span>
              {result.invalid > 0 && <span className="text-red-600">{result.invalid} with problems{result.dryRun ? '' : ' (skipped)'}</span>}
              {!result.dryRun && <span className="text-slate-600">{result.saved} saved as Ready</span>}
            </div>
            <div className="max-h-[42vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-white sticky top-0 text-[11px] uppercase text-slate-500 border-b border-slate-100">
                  <tr>
                    {!result.dryRun && (
                      <th className="px-2 py-2 w-8">
                        <input type="checkbox" checked={allTicked} className="rounded"
                          onChange={() => setSelected(allTicked ? [] : savedRows.map((x) => x.lrNo))} />
                      </th>
                    )}
                    <th className="text-left px-2 py-2 w-12">Row</th>
                    <th className="text-left px-2 py-2">LR · consignee</th>
                    <th className="text-left px-2 py-2">Appointment</th>
                    <th className="text-left px-2 py-2">PO · expiry</th>
                    <th className="text-left px-2 py-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r) => {
                    const b = booked[r.lrNo];
                    return (
                      <tr key={r.row} className={`border-t border-slate-100 align-top ${!r.ok ? 'bg-red-50/50' : ''}`}>
                        {!result.dryRun && (
                          <td className="px-2 py-2">
                            {r.saved && <input type="checkbox" className="rounded" checked={selected.includes(r.lrNo)} onChange={() => toggle(r.lrNo)} />}
                          </td>
                        )}
                        <td className="px-2 py-2 text-slate-500">{r.row}</td>
                        <td className="px-2 py-2">
                          <div className="font-mono font-medium text-slate-900">{r.lrNo || '—'}</div>
                          {r.consignee && <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{r.consignee}</div>}
                          {r.courier && <div className="text-[10px] text-slate-400">{r.courier}</div>}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div>{fmtDay(r.appointmentDate)}</div>
                          <div className="text-[11px] text-slate-500">{r.appointmentSlot ?? '—'}</div>
                          {r.appointmentId && <div className="text-[10px] text-slate-400 font-mono truncate max-w-[140px]" title={r.appointmentId}>ID {r.appointmentId}</div>}
                        </td>
                        <td className="px-2 py-2">
                          <div className="truncate max-w-[160px]" title={r.poNumber ?? ''}>{r.poNumber ?? '—'}</div>
                          <div className="text-[11px] text-slate-500">till {fmtDay(r.poExpiryDate)}</div>
                        </td>
                        <td className="px-2 py-2 max-w-[360px]">
                          {b ? (
                            <div className={`flex items-start gap-1 ${b.ok ? 'text-emerald-700' : 'text-red-600'}`}>
                              {b.ok ? <CheckCircle2 size={13} className="mt-0.5 shrink-0" /> : <XCircle size={13} className="mt-0.5 shrink-0" />}
                              <span>{b.message}{b.ok && b.appointmentRef ? <span className="block text-[10px] font-mono text-slate-500">Ref {b.appointmentRef}</span> : null}</span>
                            </div>
                          ) : (
                            <>
                              {r.ok && (
                                <div className="flex items-center gap-1 text-emerald-700">
                                  <CheckCircle2 size={13} /> {r.saved ? 'Saved — ready to book' : 'OK'}
                                </div>
                              )}
                              {r.errors.map((m, i) => (
                                <div key={`e${i}`} className="flex items-start gap-1 text-red-600"><XCircle size={12} className="mt-0.5 shrink-0" />{m}</div>
                              ))}
                              {r.warnings.map((m, i) => (
                                <div key={`w${i}`} className="flex items-start gap-1 text-amber-700"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{m}</div>
                              ))}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg mr-auto">CLOSE</button>
          <button onClick={() => run(true)} disabled={!parsed.length || busy !== null}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
            {busy === 'check' ? <Loader2 size={14} className="animate-spin" /> : <FileSearch size={14} />} Check file
          </button>
          <button onClick={() => run(false)} disabled={!result || !result.dryRun || result.valid === 0 || busy !== null}
            title={!result ? 'Check the file first' : undefined}
            className="px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/5 disabled:opacity-50">
            {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Save {result?.dryRun ? result.valid : ''} valid row{result?.dryRun && result.valid === 1 ? '' : 's'}
          </button>
          <button onClick={bookSelected} disabled={!savedRows.length || !selected.length || busy !== null}
            title={!savedRows.length ? 'Save the rows first' : undefined}
            className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90 disabled:opacity-50">
            {busy === 'book' ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
            Book {selected.length || ''} selected
          </button>
        </div>
      </div>
    </Modal>
  );
}
